// Barra de estados del marcador de grupo: cuántas unidades del grupo hay en
// cada estado. Antes el grupo era un círculo del color predominante: un «26»
// rojo no decía que 3 iban conduciendo. Primero fue un anillo cónico, pero
// era casi idéntico al de Mapon (pedido de Daniel, 21-oct-2026: «más
// propio»); ahora son segmentos de una barra bajo el número.
//
// Orden fijo (no por cantidad) para que la barra se lea igual en todos los
// grupos: lo que se mueve primero, lo que no reporta al final.
export const RING_ORDER = ["driving", "ralenti", "stopped", "sleep", "offline"] as const;

export interface ClusterSegment {
  status: (typeof RING_ORDER)[number];
  count: number;
  color: string;
}

export const clusterSegments = (
  vehicles: { status?: string | null }[],
  colors: Record<string, string>,
  fallback = "#6B7280",
): ClusterSegment[] => {
  const counts: Record<string, number> = {};
  for (const v of vehicles) {
    const s = (RING_ORDER as readonly string[]).includes(v.status ?? "") ? v.status! : "offline";
    counts[s] = (counts[s] || 0) + 1;
  }
  return RING_ORDER.filter((k) => counts[k]).map((status) => ({
    status,
    count: counts[status],
    color: colors[status] ?? fallback,
  }));
};

/** Llave estable para que el memo del marcador redibuje sólo si cambió el reparto. */
export const segmentsKey = (segments: ClusterSegment[]) =>
  segments.map((s) => `${s.status}:${s.count}`).join("|");

export interface ClusterUnitRow {
  id: string;
  name: string;
  status: (typeof RING_ORDER)[number];
  course: number;
  /** km/h crudos del equipo; el formato lo pone quien pinta (rowSpeed). */
  speed: number | null;
}

// Lista del grupo al pasar el mouse (como en Mapon): qué unidades hay sin
// tener que acercarse. Mismo orden que la barra y luego por nombre; con
// `max` filas la tarjeta no tapa medio mapa en un grupo de 126.
export const clusterUnitRows = (
  vehicles: {
    id?: string;
    fullId?: string;
    status?: string | null;
    course?: number | string | null;
    speed?: number | null;
    vehicle?: { name?: string } | null;
  }[],
  max = 6,
): { rows: ClusterUnitRow[]; more: number } => {
  const rank = (s: string) => RING_ORDER.indexOf(s as (typeof RING_ORDER)[number]);
  const all = vehicles
    .map((v, i) => {
      const status = rank(v.status ?? "") >= 0 ? (v.status as ClusterUnitRow["status"]) : "offline";
      return {
        id: v.id ?? v.fullId ?? String(i),
        name: v.vehicle?.name || v.fullId || v.id || "—",
        status,
        course: Number(v.course) || 0,
        speed: v.speed ?? null,
      };
    })
    .sort((a, b) => rank(a.status) - rank(b.status) || a.name.localeCompare(b.name, "es", { numeric: true }));
  return { rows: all.slice(0, max), more: Math.max(0, all.length - max) };
};
